import { z } from "zod";

/**
 * The SEVEN-level working spine the CDT Studio edits, before it is compressed
 * to the four levels a planogram ships.
 *
 * It is one opaque JSON document on purpose. The draft is always read and
 * written whole, it is not a node tree (it carries per-scope value orders,
 * measured shelf bands, per-SKU shelf hints and the pooling answers), and its
 * shape is still moving — so it travels as `unknown` with a `schemaVersion`
 * beside it and a forward-only adapter on the consumer side, exactly as
 * `pog_data` does. Typing it here would freeze a shape that is not ready and
 * force an omni-common release for every studio field.
 */
export const RouteGetMerchflows_merchflowId_CdtDraft = "/merchflows/:merchflowId/cdt/draft";
export const RoutePutMerchflows_merchflowId_CdtDraft = "/merchflows/:merchflowId/cdt/draft";

export const ParamsMerchflowCdtDraftSchema = z.object({
  merchflowId: z.coerce.number().int().positive(),
});

export const PutMerchflowCdtDraftSchema = z.object({
  /** 16 is `schema_version VARCHAR(16)` on `merchflow_cdt_draft`, not a taste. */
  schemaVersion: z.string().min(1).max(16),
  /**
   * The `updatedAt` the client last read, for optimistic concurrency — the same
   * lock the CDT tree PUT uses. `null` means "I believe none exists yet", and a
   * mismatch is a 409 rather than a silent overwrite. (`null` when a row DOES
   * exist is a mismatch too: the client's belief is wrong, so it is a 409, never
   * an overwrite of somebody else's draft.)
   *
   * COMPARE IN JAVASCRIPT, NOT IN SQL. Read the row with
   * `SELECT ... FOR UPDATE`, apply the same lossy conversion to both sides
   * (`Date.toISOString()`), compare the strings, then update. A SQL equality
   * test looks tidier and is silently broken: `timestamptz` holds microseconds
   * and `toISOString()` emits milliseconds, so the truncated string never equals
   * the stored value and EVERY save is refused — including the legitimate one,
   * with no clue why. `FOR UPDATE` holds the row for the rest of the transaction
   * so the check cannot be raced.
   *
   * STAMP WITH `clock_timestamp()`, NOT `NOW()`. `NOW()` is transaction-scoped
   * in Postgres — every call inside one transaction returns the same instant —
   * so two saves sharing a transaction would be stamped identically and the lock
   * could not tell a stale read from a current one. One transaction per request
   * makes `NOW()` usually work; `clock_timestamp()` makes it correct.
   */
  updatedAt: z.iso.datetime().nullable(),
  /**
   * The base pog the draft's measurements came from. Recorded here, not keyed
   * on: the draft is stored per merchflow, and changing this does not move it.
   *
   * It is NOT behaviourally inert everywhere, though — the same field on the
   * compression POST switches the unresolved-combos guarantee on and off. See
   * the note on `basePogId` in `./compression.ts` before treating it as
   * bookkeeping.
   */
  basePogId: z.number().int().positive().nullable(),
  draft: z.unknown(),
});

export const ResponseMerchflowCdtDraftSchema = z.object({
  merchflowId: z.number(),
  schemaVersion: z.string(),
  revision: z.number(),
  /**
   * Plain `z.string()` rather than `z.iso.datetime()`, matching
   * `api/cdt/trees/_treeId_/index.ts`. It round-trips correctly because the
   * server emits `.toISOString()`; the asymmetry with the request above is the
   * neighbour's convention, not an oversight.
   */
  updatedAt: z.string(),
  basePogId: z.number().nullable(),
  draft: z.unknown(),
});

export type ParamsMerchflowCdtDraft = z.infer<typeof ParamsMerchflowCdtDraftSchema>;
export type PutMerchflowCdtDraft = z.infer<typeof PutMerchflowCdtDraftSchema>;
export type ResponseGetMerchflowCdtDraft = z.infer<typeof ResponseMerchflowCdtDraftSchema>;
export type ResponsePutMerchflowCdtDraft = ResponseGetMerchflowCdtDraft;
